import { v1 as uuidv1 } from 'uuid';
import { find, get, flatMap, filter } from 'lodash';
import questionnaireMessages from 'containers/QuestionnairePage/messages';
import {
  ANSWER_DEFAULT,
  ANSWER_NONE_OF_THE_ABOVE,
  ANSWER_OTHER,
} from 'containers/QuestionnairePage/constants';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { isSingleQuestionType } from 'utils/nodeTypes';
import { getActiveValues as getAllowedScreeningAnswers } from 'components/ScreeningQuestion/utils';
import { RANKING_LABEL_SORT_OPTIONS } from './constants';

export const getEmptyAnswer = (valid = false) => ({
  body: '',
  refId: uuidv1(),
  type: ANSWER_DEFAULT,
  isEmptyAnswer: !valid,
});

export const getNoneOfTheAboveEmptyAnswer = ({ body = '', alias = '' }) => ({
  body,
  alias,
  refId: uuidv1(),
  type: ANSWER_NONE_OF_THE_ABOVE,
});

export const getOtherEmptyAnswer = ({ body = '', label = '', alias = '' }) => ({
  type: ANSWER_OTHER,
  body,
  label,
  alias,
  refId: uuidv1(),
  required: false,
});

export const carriedForwardAnswersSelector = (answers) =>
  answers.filter(isCarriedForward);

export const defaultAnswersSelector = (answers) =>
  answers.filter((answer) => answer.type === ANSWER_DEFAULT);

export const emptyAnswersSelector = (answers) =>
  answers.filter((answer) => answer.isEmptyAnswer);

export const noneOfTheAboveAnswerSelector = (answers) =>
  find(answers, (answer) => answer.type === ANSWER_NONE_OF_THE_ABOVE);

export const otherSelector = (answers) =>
  find(answers, (answer) => answer.type === ANSWER_OTHER);

export const isAnswerEmpty = (answer) => {
  const prompt = getPromptFromHtml(answer.body);
  return prompt === '' && !(answer.media && answer.media.url);
};

export const isCarriedForward = (answer) => Boolean(answer.isCarriedForward);
export const hasOtherAnswer = (answers) => Boolean(otherSelector(answers));
export const isAnswerExclusive = (answer) =>
  Boolean(answer.isExclusive || answer.type === ANSWER_NONE_OF_THE_ABOVE);

export const addOtherAnswer = (answers, intl, isScreening) => [
  ...answers,
  getOtherEmptyAnswer({
    body: intl.formatMessage(questionnaireMessages.addOtherPlaceholder),
    label: intl.formatMessage(questionnaireMessages.addOtherPrompt),
    alias: intl.formatMessage(questionnaireMessages.addOtherAlias),
    isScreening,
  }),
];

export const hasNoneOfTheAboveAnswer = (answers) =>
  Boolean(noneOfTheAboveAnswerSelector(answers));

export const addNoneOfTheAboveAnswer = (answers, intl, isScreening) => [
  ...answers,
  getNoneOfTheAboveEmptyAnswer({
    body: intl.formatMessage(questionnaireMessages.noneOfTheAbovePlaceholder),
    alias: intl.formatMessage(questionnaireMessages.noneOfTheAboveAlias),
    isScreening,
  }),
];

export const removeNoneOfTheAboveAnswers = (answers) =>
  answers.filter((answer) => answer.type !== ANSWER_NONE_OF_THE_ABOVE);

export const removeOtherAnswers = (answers) =>
  answers.filter((answer) => answer.type !== ANSWER_OTHER);

export const getRankingIsHighFirst = (sort) =>
  sort === RANKING_LABEL_SORT_OPTIONS.DESC;

export const getActiveAnswerValues = (values) =>
  filter(
    values,
    ({ body, media }) => body || get(media, 'url', false) !== false,
  );

export const canEnableQuotasForAnswers = ({
  answers,
  isUnderEdit,
  isScreenerActive,
  isScreening,
}) => {
  if (isScreening && isUnderEdit && !isScreenerActive) {
    return false;
  }

  const allowedAnswers = isScreening
    ? getAllowedScreeningAnswers(answers)
    : getActiveAnswerValues(answers);
  const canEnableQuotas =
    allowedAnswers.length > 1 && allowedAnswers.length < 101;

  return canEnableQuotas;
};

export const hasQuotas = (answers) => {
  const allowedAnswers = getAllowedScreeningAnswers(answers);

  return allowedAnswers.length > 1 && allowedAnswers.length < 101;
};

export const canEnableQuotasForQuestion = (
  { isScreening, isScreenerActive, type, answers },
  isUnderEdit,
) => {
  if ((!isSingleQuestionType(type) && isScreening) || answers.length === 0) {
    return false;
  }

  return canEnableQuotasForAnswers({
    answers,
    isUnderEdit,
    isScreenerActive,
    isScreening,
  });
};

export function getPreviousNodeRefIdsForNode({
  refId,
  isScreening = false,
  previousNodesPerNode,
  nodes,
  screeningNodes,
}) {
  // when screeningLogicEnabled `screeningNodes` is empty
  // and `nodes` contain both screening and normal nodes
  // and screening nodes are at the top of the list array
  const hasScreeningQuestionWithLogic = !!nodes[0]?.isScreening;

  let screeningQuestionsPath;
  if (hasScreeningQuestionWithLogic) {
    if (isScreening) {
      // when we check for a screening node the previous
      // screening nodes are retrieved from previousNodesForNode by screeningQl
      screeningQuestionsPath = [];
    } else {
      screeningQuestionsPath = flatMap(
        nodes.filter((node) => node.isScreening),
        'refId',
      );
    }
  } else {
    screeningQuestionsPath = flatMap(screeningNodes, 'refId');
  }

  // find if one of the carryforward nodes is completely skipped
  const previousNodesForNode = previousNodesPerNode[refId] || [];

  return [...screeningQuestionsPath, ...previousNodesForNode];
}
